import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import API from '../services/api';

function AdminClientIntakeDataPage() {
  const { clientId } = useParams();
  const [intakeList, setIntakeList] = useState([]);
  const [errorMsg, setErrorMsg] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchIntakeData();
  }, [clientId]);

  // ─────────────────────────────────────────────────────────────
  // DATA FETCH
  // ─────────────────────────────────────────────────────────────
  const fetchIntakeData = async () => {
    try {
      const res = await API.get(`/forms/admin/intake-data/${clientId}`);
      setIntakeList(res.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching intake data:', err);
      setErrorMsg('Could not fetch intake data');
      setLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // HELPERS
  // ─────────────────────────────────────────────────────────────
  /** Render a text or Base‑64 image signature. */
  const renderSig = (sig) =>
    sig?.startsWith('data:image') ? (
      <img
        src={sig}
        alt="Signature"
        style={{ maxWidth: '300px', border: '1px solid var(--border-color)' }}
      />
    ) : (
      <span>{sig}</span>
    );

  /** Nicely group the page‑3‑to‑5 fields if present, else fallback. */
  const renderStructuredFields = (data) => {
    // pull‑out helpers (adjust key names to match your backend as needed)
    const val = (k) => data?.[k] ?? '';

    /* ╭─────────────────────────────────────────╮
       │            CONSENT  TO  TREAT           │
       ╰─────────────────────────────────────────╯ */
    const hasConsent = val('consentPatientName') || val('consentSignature');
    /* ╭─────────────────────────────────────────╮
       │          CANCELLATION  POLICY          │
       ╰─────────────────────────────────────────╯ */
    const hasCancel = val('cancelSignature');
    /* ╭─────────────────────────────────────────╮
       │     LIMITS  OF  CONFIDENTIALITY        │
       ╰─────────────────────────────────────────╯ */
    const hasLimits = val('limitsSignature');
    /* ╭─────────────────────────────────────────╮
       │           OTHER  PROVISIONS            │
       ╰─────────────────────────────────────────╯ */
    const hasOther = val('otherSignature');

    if (!(hasConsent || hasCancel || hasLimits || hasOther)) return null;

    return (
      <>
        {/* CONSENT TO TREAT */}
        {hasConsent && (
          <section style={{ marginTop: '1rem' }}>
            <h4>Consent to Treat</h4>
            {val('consentPatientName') && (
              <p>
                <strong>Patient Name(s):</strong> {val('consentPatientName')}
              </p>
            )}
            {val('consentDate') && (
              <p>
                <strong>Date:</strong> {val('consentDate')}
              </p>
            )}
            {val('consentSignature') && (
              <>
                <p>
                  <strong>Signature:</strong>
                </p>
                {renderSig(val('consentSignature'))}
              </>
            )}
          </section>
        )}

        {/* CANCELLATION POLICY */}
        {hasCancel && (
          <section style={{ marginTop: '1rem' }}>
            <h4>Cancellation Policy</h4>
            {val('cancelFee') && (
              <p>
                <strong>Rescheduling Fee:</strong> ${val('cancelFee')}
              </p>
            )}
            {val('cancelDate') && (
              <p>
                <strong>Date:</strong> {val('cancelDate')}
              </p>
            )}
            <p>
              <strong>Signature:</strong>
            </p>
            {renderSig(val('cancelSignature'))}
          </section>
        )}

        {/* LIMITS OF CONFIDENTIALITY */}
        {hasLimits && (
          <section style={{ marginTop: '1rem' }}>
            <h4>Limits of Confidentiality – Acknowledgement</h4>
            {val('limitsDate') && (
              <p>
                <strong>Date:</strong> {val('limitsDate')}
              </p>
            )}
            <p>
              <strong>Signature:</strong>
            </p>
            {renderSig(val('limitsSignature'))}
          </section>
        )}

        {/* OTHER PROVISIONS */}
        {hasOther && (
          <section style={{ marginTop: '1rem' }}>
            <h4>Other Provisions</h4>
            {val('otherHomePhone') && (
              <p>
                <strong>Home Phone:</strong> {val('otherHomePhone')}
              </p>
            )}
            {val('otherWorkPhone') && (
              <p>
                <strong>Work Phone:</strong> {val('otherWorkPhone')}
              </p>
            )}
            {val('otherEmail') && (
              <p>
                <strong>Email:</strong> {val('otherEmail')}
              </p>
            )}
            {val('otherDate') && (
              <p>
                <strong>Date:</strong> {val('otherDate')}
              </p>
            )}
            <p>
              <strong>Signature:</strong>
            </p>
            {renderSig(val('otherSignature'))}
          </section>
        )}
      </>
    );
  };

  // ─────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────
  if (loading) return <p>Loading...</p>;
  if (errorMsg) return <p style={{ color: 'red' }}>{errorMsg}</p>;
  if (!intakeList.length) return <p>No intake submissions found.</p>;

  return (
    <div>
      <h3>Intake Submissions for Client {clientId}</h3>

      {intakeList.map((item) => {
        const { id, created_at, signature, data_json } = item;
        return (
          <div
            key={id}
            style={{
              border: '1px solid var(--border-color)',
              margin: '1rem 0',
              padding: '1rem'
            }}
          >
            <p>
              <strong>Submitted At:</strong>{' '}
              {new Date(created_at).toLocaleString()}
            </p>

            {/* TOP‑LEVEL SIGNATURE (if the form itself captured one) */}
            {signature && (
              <>
                <p>
                  <strong>Form Signature:</strong>
                </p>
                {renderSig(signature)}
              </>
            )}

            {/* CUSTOM SECTIONS FOR PAGES 3‑5 */}
            {renderStructuredFields(data_json)}

            {/* fallback table for everything else */}
            <table
              style={{
                borderCollapse: 'collapse',
                width: '100%',
                marginTop: '1rem'
              }}
            >
              <thead>
                <tr>
                  <th style={th}>Field</th>
                  <th style={th}>Value</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(data_json).map(([key, value]) => (
                  <tr key={key}>
                    <td style={td}>{key}</td>
                    <td style={td}>
                      {typeof value === 'object'
                        ? JSON.stringify(value, null, 2)
                        : value}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}

/* simple cell styles */
const th = { border: '1px solid var(--border-color)', padding: 8, background: '#f6f6f6' };
const td = { border: '1px solid var(--border-color)', padding: 8 };

export default AdminClientIntakeDataPage;
